import Error from '@components/error';
import { Button, Form } from '@components/ui';
import { useAppSelector } from '@hooks/app/redux';
import { AuthTitleSC } from '@pages/auth/auth.styled';
import { STEPS } from '@pages/auth/registration/registration.types';
import { actions } from '@store/ducks/auth';
import JwtDecode from 'jwt-decode';
import moment from 'moment';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory } from 'react-router-dom';
import ReactCodeInput from 'react-verification-code-input';
import { IDecodedLoginToken } from 'types/auth-token.types';
import { useTimer } from 'use-timer';
import Header from '../../../components/header';
import { initialValues, TFormValues, validationSchema } from './step-code.schema';
import { StepCodeSC } from './step-code.styled';

const StepCode: React.FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { replace } = useHistory();

  const isLoading = useAppSelector((store) => store.auth.registerLoading);
  const emailError = useAppSelector((store) => store.auth.registerError);
  const isEmailResent = useAppSelector((store) => store.auth.hasResentEmail);
  const resendEmailError = useAppSelector((store) => store.auth.resentEmailError);
  const registerToken = useAppSelector((store) => store.auth.registerToken);

  const resetRegistration = React.useCallback(() => {
    dispatch(actions.clearRegisterData());
    dispatch(actions.setCurrentRegStep(STEPS.LANDING));
    replace('/registration');
  }, []);

  React.useEffect(() => {
    if (!registerToken && process.env.NODE_ENV !== 'development') {
      resetRegistration();
    }
  }, []);

  const onSubmit = async ({ code }: TFormValues) => {
    dispatch(actions.getPhoneCodeVerifyRequest(code));
  };

  const handleEmailResend = async () => {
    dispatch(actions.getResendPhoneRequest());
  };

  const [, formOptions] = Form.useForm({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const { phoneNumber, timeForSubmit } = React.useMemo(() => {
    try {
      const decoded = JwtDecode<IDecodedLoginToken>(registerToken);
      const diff = moment.unix(decoded.exp).diff(moment(), 'second');

      return {
        timeForSubmit: diff < 0 ? 0 : diff,
        phoneNumber: decoded.phoneNumber,
      };
    } catch (error) {
      return {
        timeForSubmit: 0,
        phoneNumber: '',
      };
    }
  }, [registerToken]);

  const { time } = useTimer({
    initialTime: timeForSubmit,
    timerType: 'DECREMENTAL',
    autostart: true,
    endTime: 0,
    onTimeOver: resetRegistration,
  });

  return (
    <>
      <Prompt
        when={Boolean(registerToken)}
        message={formatMessage({ id: 'reg.step_1.leave_message' })}
      />

      <div className="form-wrapper" style={{ paddingTop: 60 }}>
        <Header textId="reg.step_2.title" />
        <StepCodeSC className="right-content">
          <Form {...formOptions} className="form" showFeedback={false}>
            <AuthTitleSC>
              <FormattedMessage id="reg.step_2.form_title" />
            </AuthTitleSC>
            <p className="insert-code-text">
              <FormattedMessage
                id="reg.step_2.copy"
                values={{ phone: <span className="insert-code-email">{phoneNumber}</span> }}
              />
            </p>
            <div className="input-wrapper">
              <Form.Field name="code" getValueFromEvent={(v) => v}>
                <ReactCodeInput
                  type="number"
                  fields={6}
                  className="verification-code-input"
                  required
                />
              </Form.Field>
              {emailError ? (
                <Error
                  message={formatMessage({ id: `reg.step_2.auth_error.${emailError}` })}
                  className="code-error"
                />
              ) : null}
            </div>
            <Button
              type="submit"
              loading={isLoading}
              className="submit-button-code"
              style={{ marginTop: 16 }}
            >
              <FormattedMessage id={'reg.step_2.submit'} />{' '}
              {`${Math.floor((time % 3600) / 60)
                .toString()
                .padStart(2, '0')}:${(time % 60).toString().padStart(2, '0')}`}
            </Button>

            {resendEmailError ? (
              <Error
                message={formatMessage({ id: `reg.step_2.auth_error.${resendEmailError}` })}
                className="code-error resend-email-error"
              />
            ) : (
              <div className="resend-email" onClick={handleEmailResend} role="button">
                <span className="resend-email_txt">
                  <FormattedMessage
                    id={isEmailResent ? 'reg.step_2.resend_sms_success' : 'reg.step_2.no_sms'}
                  />
                </span>
                <span className="resend-email_btn" role="button">
                  <FormattedMessage id="reg.step_2.resend_sms" />
                </span>
              </div>
            )}
          </Form>
        </StepCodeSC>
      </div>
    </>
  );
};

export default StepCode;
